import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Buildings, ArrowSquareOut, Key, Copy, Trash, Eye, EyeSlash, ClockCounterClockwise, Sparkle, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function GovPortals() {
  const [portals, setPortals] = useState([]);
  const [recent, setRecent] = useState([]);
  const [editing, setEditing] = useState(null);   // portal object being edited
  const [form, setForm] = useState({ username: "", password: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [reveal, setReveal] = useState(null); // { portal, username, password }

  const load = async () => {
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        api.get("/portals"),
        api.get("/portals/recent"),
      ]);
      setPortals(p);
      setRecent(r);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const groups = portals.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const openEdit = (p) => {
    setEditing(p);
    setForm({ username: "", password: "", notes: p.notes || "" });
    setShowPwd(false);
  };

  const save = async () => {
    if (!form.username.trim()) { toast.error("Username required"); return; }
    setSaving(true);
    try {
      await api.post("/portals/save", { portal_id: editing.id, ...form });
      toast.success("Credentials saved (encrypted at rest)");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const forget = async (p) => {
    if (!window.confirm(`Forget saved credentials for ${p.label}?`)) return;
    try { await api.delete(`/portals/${p.id}`); toast.success("Forgotten"); load(); }
    catch { toast.error("Failed"); }
  };

  const launch = async (p) => {
    // Log the launch (branch-scoped) + open in a new tab
    try { await api.post("/portals/launch", { portal_id: p.id }); } catch {}
    window.open(p.url, "_blank", "noopener,noreferrer");
    setTimeout(load, 500);
  };

  const revealCreds = async (p) => {
    try {
      const { data } = await api.post(`/portals/${p.id}/reveal`);
      setReveal({ portal: p, ...data });
    } catch { toast.error("Nothing saved for this portal yet"); }
  };

  const copy = (s, label) => {
    navigator.clipboard.writeText(s || "");
    toast.success(`${label} copied`);
  };

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="gov-portals-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Buildings size={14} weight="fill" className="text-orange-500" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Government Portal Login Manager</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Portal Center</h1>
        <p className="text-sm text-slate-500 mt-1">One-tap launcher for {portals.length} government portals · Encrypted credential vault · Copy Consumer / Application numbers in one click.</p>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-6 text-xs text-amber-900 flex items-start gap-2" data-testid="gp-notice">
        <LockKey size={16} weight="duotone" className="mt-0.5 shrink-0" />
        <div>
          <b>Honest limits:</b> The ERP cannot bypass CAPTCHA, OTP or automatic logout — those are the portal's security controls.
          Saved usernames and passwords are stored with <b>AES-256-GCM encryption</b>. Reveal is audit-logged.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{group}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((p) => (
                  <Card key={p.id} className="p-4 bg-white border-slate-200 hover:border-slate-300 transition-all" data-testid={`gp-card-${p.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: p.color }}>{p.logo}</div>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{p.label}</div>
                          <div className="text-[10px] text-slate-500">{p.id_label}</div>
                        </div>
                      </div>
                      {p.saved ? (
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`gp-saved-${p.id}`}>● Saved</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500 border-slate-200">○ Not saved</Badge>
                      )}
                    </div>
                    {p.saved && (
                      <div className="text-[11px] text-slate-500 mb-3 font-mono">
                        {p.username_masked}
                        {p.has_password && <span className="ml-2 text-slate-400">🔒 password saved</span>}
                      </div>
                    )}
                    <div className="flex gap-1.5 flex-wrap">
                      <Button size="sm" onClick={() => launch(p)} className="bg-orange-600 hover:bg-orange-700 text-white gap-1 h-8 text-xs" data-testid={`gp-launch-${p.id}`}>
                        <ArrowSquareOut size={12} /> Open
                      </Button>
                      {p.saved ? (
                        <Button size="sm" variant="outline" onClick={() => revealCreds(p)} className="gap-1 h-8 text-xs" data-testid={`gp-reveal-${p.id}`}>
                          <Eye size={12} /> Reveal
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="gap-1 h-8 text-xs" data-testid={`gp-edit-${p.id}`}>
                        <Key size={12} /> {p.saved ? "Update" : "Save creds"}
                      </Button>
                      {p.saved && (
                        <Button size="icon" variant="ghost" onClick={() => forget(p)} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid={`gp-forget-${p.id}`}>
                          <Trash size={13} />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <Card className="p-4 bg-white border-slate-200 sticky top-6" data-testid="gp-recent-launches">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
              <ClockCounterClockwise size={11} /> Recent launches
            </div>
            {recent.length === 0 ? (
              <div className="text-xs text-slate-400 py-6 text-center">No launches yet in your branch.</div>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {recent.map((r) => (
                  <div key={r.id} className="text-xs border-b border-slate-100 pb-2 last:border-0">
                    <div className="font-medium text-slate-800">{r.portal_label}</div>
                    <div className="text-slate-500">{r.operator}</div>
                    <div className="text-[10px] text-slate-400">{r.at ? new Date(r.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Save creds dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="gp-save-dialog">
          <DialogHeader><DialogTitle>Save {editing?.label} credentials</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Username / Login ID</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. rakesh@example.com or 9876543210" data-testid="gp-form-username" autoFocus /></div>
            <div>
              <Label>Password (optional)</Label>
              <div className="flex gap-2">
                <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kept encrypted at rest" data-testid="gp-form-password" />
                <Button variant="outline" size="icon" onClick={() => setShowPwd(!showPwd)} className="shrink-0">
                  {showPwd ? <EyeSlash size={15} /> : <Eye size={15} />}
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Leave blank to only remember the username.</div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Branch A account · reset on 5th monthly" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="gp-form-save">{saving ? "Saving…" : "Save (Encrypted)"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal dialog */}
      <Dialog open={!!reveal} onOpenChange={(v) => !v && setReveal(null)}>
        <DialogContent className="bg-white" data-testid="gp-reveal-dialog">
          <DialogHeader><DialogTitle>Credentials · {reveal?.portal?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-slate-50 p-3 border border-slate-200 font-mono text-sm">
              <div className="flex justify-between items-center">
                <span><span className="text-slate-500 text-xs">Username:</span> {reveal?.username}</span>
                <Button size="sm" variant="ghost" onClick={() => copy(reveal.username, "Username")} className="gap-1 text-xs"><Copy size={12} /> Copy</Button>
              </div>
              {reveal?.password && (
                <div className="flex justify-between items-center mt-2">
                  <span><span className="text-slate-500 text-xs">Password:</span> {reveal.password}</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(reveal.password, "Password")} className="gap-1 text-xs"><Copy size={12} /> Copy</Button>
                </div>
              )}
            </div>
            <div className="text-[10px] text-slate-500 italic">This reveal has been audit-logged. Never share credentials over WhatsApp or email.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReveal(null)}>Close</Button>
            <Button onClick={() => { launch(reveal.portal); setReveal(null); }} className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5" data-testid="gp-open-after-reveal">
              <ArrowSquareOut size={13} /> Open Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
